import { useCallback } from 'react';
import type { ReactElement } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';
import { VStack } from '@cbhq/cds-web/layout';
import { ActionType, ComponentType, logEvent } from '@cbhq/client-analytics';
import type { ErrorMetadata } from '@cbhq/error-vitals-web';
import { ErrorBoundary } from '@cbhq/error-vitals-web';

import { BasicErrorFallback } from '../components/ErrorBoundary/BasicErrorFallback';
import { NavigationContentSpinner } from '../components/Navigation';
import { useOrganization } from '../contexts/OrganizationContext';
import { SettingsPage } from '../pages/Settings/pages/SettingsPage';
import { ROUTE_SETTINGS } from '../pages/Settings/utils/constants';
import { ROUTE_TEAM } from '../pages/Team/utils/constants';
import { AppExperiments, useExperiments, useIsTreatment } from '../utils/experiments';

const SETTINGS_ERROR_BOUNDARY_DEFAULT_METADATA: ErrorMetadata = {
  context: 'error-boundary',
  severity: 'error',
  isHandled: true,
};

function SettingsRoutes(): ReactElement {
  const { isLoaded } = useExperiments();
  const isWorkspaceSettingsTreatment = useIsTreatment(AppExperiments.t0018_3ab23203);
  const shouldShowSettings = isWorkspaceSettingsTreatment;

  if (!isLoaded) {
    return <NavigationContentSpinner />;
  }

  if (!shouldShowSettings) {
    return <Navigate to={ROUTE_TEAM} replace />;
  }

  return (
    <Routes>
      <Route index element={<SettingsPage />} />
      <Route path="*" element={<Navigate to={ROUTE_SETTINGS} replace />} />
    </Routes>
  );
}

export default function Settings(): ReactElement {
  const { selectedOrganization } = useOrganization();
  const handleCatch = useCallback((error: Error) => {
    logEvent('settings_management_error', {
      action: ActionType.measurement,
      componentType: ComponentType.page,
      error: error.message,
    });
  }, []);

  return (
    <VStack width="100%" height="100%">
      <ErrorBoundary
        // Remount the boundary when the org changes so a stale cached error
        // for the previous org doesn't keep the fallback stuck after switching.
        key={selectedOrganization?.organizationId}
        name="settings-boundary"
        defaultMetadata={SETTINGS_ERROR_BOUNDARY_DEFAULT_METADATA}
        fallback={<BasicErrorFallback />}
        onCatch={handleCatch}
      >
        <SettingsRoutes />
      </ErrorBoundary>
    </VStack>
  );
}
